import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { usePageMeta } from "@/lib/usePageMeta";
import {
  ArrowLeft, ArrowRight, Loader2, CheckCircle2, AlertTriangle,
  Copy, Phone, Building2, Receipt, ShieldCheck, User,
} from "lucide-react";
import { PRICING_CATEGORIES } from "@/data/pricing";
import { parsePriceLabel, formatCzk } from "@/lib/price";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE = "+420 775 659 129";

const ALL_SERVICES = PRICING_CATEGORIES.flatMap((cat) =>
  cat.items.map((it) => ({
    ...it,
    category: cat.label,
    category_key: cat.key,
    parsed: parsePriceLabel(it.price),
  }))
);

const VEHICLE_TYPE_OPTIONS = [
  { id: "osobni", label: "Osobní" },
  { id: "dodavkova", label: "Dodávková" },
  { id: "flotila", label: "Firemní flotila" },
  { id: "jine", label: "Jiné" },
];

const NEEDS_OPTIONS = [
  { id: "stk", label: "STK" },
  { id: "emise", label: "Emise" },
  { id: "ek", label: "Evidenční kontrola" },
  { id: "prepis", label: "Přepis vozidla" },
  { id: "vyzvednuti", label: "Vyzvednutí vozidla" },
  { id: "vraceni", label: "Vrácení vozidla zpět" },
  { id: "servis", label: "Servisní příprava" },
  { id: "pripominky", label: "Připomínky budoucích termínů" },
];

const INITIAL_FORM = {
  client_type: "person", // "person" | "company"
  name: "", phone: "", email: "",
  company_name: "", ico: "", dic: "", billing_address: "",
  contact_person: "", vehicle_count: "",
  vehicle_types: [], biz_needs: [],
  pickup_address: "", return_address: "",
  vehicle: "", spz_vin: "", location: "Znojmo", preferred_term: "",
  note: "", custom_amount: "", gdpr_consent: false,
};

export default function OrderPage() {
  usePageMeta({
    title: "Objednávka STK a auto služeb | STK Komfort Znojmo",
    description:
      "Objednejte si STK, emise, evidenční kontrolu, přepis vozidla nebo další auto služby ve Znojmě a okolí. STK Komfort Znojmo vám pomůže vše vyřešit pohodlně.",
    canonical: "https://www.stkkomfort.cz/objednavka",
  });
  const [params] = useSearchParams();
  const presetServiceId = params.get("service");
  const presetClient = params.get("klient");

  const [selected, setSelected] = useState(() =>
    ALL_SERVICES.find((s) => s.id === presetServiceId) || null
  );
  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
    client_type: presetClient === "firma" ? "company" : "person",
  }));
  const [showBilling, setShowBilling] = useState(presetClient === "firma");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState(null);

  const effectiveAmount = useMemo(() => {
    if (!selected) return null;
    const custom = parseFloat((form.custom_amount || "").replace(",", "."));
    if (!Number.isNaN(custom) && custom > 0) return custom;
    return selected.parsed.amount;
  }, [selected, form.custom_amount]);

  const requiresCustomAmount = selected && selected.parsed.amount == null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleArrayItem = (field, id) => {
    setForm((f) => {
      const cur = f[field] || [];
      return {
        ...f,
        [field]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
      };
    });
  };

  const setClientType = (t) => {
    setForm((f) => ({ ...f, client_type: t }));
    if (t === "company") setShowBilling(true);
  };

  const isCompany = form.client_type === "company";

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!selected) { setErrorMsg("Vyberte prosím službu."); return setStatus("error"); }
    if (!form.name.trim() || !form.phone.trim()) {
      setErrorMsg("Vyplňte jméno a telefon."); return setStatus("error");
    }
    if (!form.gdpr_consent) {
      setErrorMsg("Potvrďte prosím souhlas se zpracováním osobních údajů."); return setStatus("error");
    }
    if (!effectiveAmount || effectiveAmount <= 0) {
      setErrorMsg("Zadejte prosím konkrétní částku k úhradě (cena je individuální)."); return setStatus("error");
    }

    setStatus("loading");
    try {
      const payload = {
        client_type: form.client_type,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        company_name: form.company_name.trim() || null,
        ico: form.ico.trim() || null,
        dic: form.dic.trim() || null,
        billing_address: form.billing_address.trim() || null,
        contact_person: isCompany ? (form.contact_person.trim() || null) : null,
        vehicle_count: isCompany && form.vehicle_count
          ? parseInt(form.vehicle_count, 10) || null : null,
        vehicle_types: isCompany && form.vehicle_types.length ? form.vehicle_types : null,
        biz_needs: isCompany && form.biz_needs.length ? form.biz_needs : null,
        pickup_address: isCompany ? (form.pickup_address.trim() || null) : null,
        return_address: isCompany ? (form.return_address.trim() || null) : null,
        service_id: selected.id,
        service_name: selected.name,
        service_category: selected.category,
        price_label: selected.price,
        amount: Math.round(effectiveAmount * 100) / 100,
        vehicle: form.vehicle.trim() || null,
        spz_vin: form.spz_vin.trim() || null,
        location: form.location.trim() || null,
        preferred_term: form.preferred_term.trim() || null,
        note: form.note.trim() || null,
        gdpr_consent: true,
      };
      Object.keys(payload).forEach((k) => payload[k] === null && delete payload[k]);
      const { data } = await axios.post(`${API}/payment-orders`, payload);
      setResult(data);
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === "string" ? detail : "Objednávku se nepodařilo odeslat. Zkuste to znovu nebo zavolejte.");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0D17] text-slate-200">
      <header className="border-b border-white/10 bg-[#0A0D17]/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="order-home-link">
            <span className="block h-11 w-11 rounded-xl bg-[#0A0D17] ring-1 ring-amber-300/20"
              style={{ backgroundImage: "url(/brand/logo.png)", backgroundRepeat: "no-repeat", backgroundSize: "150% auto", backgroundPosition: "50% 18%" }} />
            <div className="leading-tight">
              <div className="font-heading text-base font-bold text-white">STK Komfort</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/80">Znojmo</div>
            </div>
          </Link>
          <a href={`tel:${PHONE.replace(/\s+/g, "")}`} className="hidden sm:inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-amber-300 to-amber-500 px-4 py-2.5 text-sm font-bold text-slate-900">
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-amber-300/80 hover:text-amber-300" data-testid="order-back-link">
          <ArrowLeft className="h-4 w-4" /> Zpět na úvod
        </Link>

        {status === "success" && result ? (
          <PaymentSuccess result={result} onNew={() => { setStatus("idle"); setResult(null); setForm(INITIAL_FORM); setSelected(null); }} />
        ) : (
          <>
            <h1 className="font-heading mt-7 text-4xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Objednávka <span className="bg-gradient-to-b from-amber-200 to-amber-500 bg-clip-text text-transparent">služby</span>
            </h1>
            <p className="mt-4 text-lg text-slate-400 max-w-2xl">
              Vyberte službu, vyplňte krátké údaje a obratem dostanete QR platbu s variabilním symbolem. Náš tým se vám ozve do hodiny.
            </p>

            <div className="mt-12 grid lg:grid-cols-12 gap-8">
              {/* Service picker */}
              <aside className="lg:col-span-5 xl:col-span-4">
                <div className="rounded-3xl border border-amber-300/15 bg-white/[0.03] p-5">
                  <div className="text-xs uppercase tracking-[0.18em] text-amber-300/90 font-semibold">1. Vyberte službu</div>
                  <div className="mt-4 max-h-[520px] overflow-y-auto pr-1 space-y-2">
                    {ALL_SERVICES.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSelected(s)}
                        data-testid={`service-pick-${s.id}`}
                        className={[
                          "w-full text-left rounded-2xl border p-4 transition-all",
                          selected?.id === s.id
                            ? "border-amber-300/60 bg-amber-300/10"
                            : "border-white/10 bg-white/[0.02] hover:border-amber-300/30",
                        ].join(" ")}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-white">{s.name}</div>
                            <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-amber-300/70">{s.category}</div>
                          </div>
                          <div className="font-heading text-base font-bold text-amber-200 whitespace-nowrap">{s.price}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </aside>

              {/* Form */}
              <form onSubmit={submit} className="lg:col-span-7 xl:col-span-8 space-y-6" data-testid="order-form">
                {selected && (
                  <div className="rounded-3xl border border-amber-300/30 bg-amber-300/5 p-5 flex items-start gap-4">
                    <Receipt className="h-5 w-5 text-amber-300 mt-1 shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-[0.18em] text-amber-300/90 font-semibold">Vybráno</div>
                      <div className="mt-1 font-heading text-lg font-bold text-white">{selected.name}</div>
                      <div className="mt-1 text-xs text-slate-400">{selected.description}</div>
                      <div className="mt-2 text-sm">
                        Orientační cena: <span className="font-bold text-amber-200">{selected.price}</span>
                        {selected.parsed.from && <span className="text-slate-400"> (od)</span>}
                      </div>
                    </div>
                  </div>
                )}

                <Section title="2. Typ klienta">
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: "person", label: "Soukromá osoba", Icon: User },
                      { id: "company", label: "Firma / podnikatel", Icon: Building2 },
                    ].map(({ id, label, Icon }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setClientType(id)}
                        data-testid={`order-client-type-${id}`}
                        className={[
                          "rounded-2xl border p-4 text-left transition-all flex items-center gap-3",
                          form.client_type === id
                            ? "border-amber-300/60 bg-amber-300/10 text-white"
                            : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-amber-300/30",
                        ].join(" ")}
                      >
                        <span className={[
                          "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border",
                          form.client_type === id
                            ? "border-amber-300/40 bg-amber-300/10 text-amber-300"
                            : "border-white/10 bg-white/[0.03] text-slate-400",
                        ].join(" ")}>
                          <Icon className="h-5 w-5" />
                        </span>
                        <div>
                          <div className="text-sm font-semibold">{label}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            {id === "person" ? "Pro vás osobně" : "Pro firmu, IČO, flotila"}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </Section>

                <Section title="3. Kontaktní údaje *">
                  <Grid>
                    <Field label="Jméno a příjmení *" name="name" value={form.name} onChange={handleChange} placeholder="Jan Novák" required testid="order-name" />
                    <Field label="Telefon *" name="phone" value={form.phone} onChange={handleChange} placeholder="+420 ___ ___ ___" type="tel" required testid="order-phone" />
                    <Field label="E-mail" name="email" value={form.email} onChange={handleChange} placeholder="jan@email.cz" type="email" className="sm:col-span-2" testid="order-email" />
                  </Grid>
                </Section>

                {isCompany && (
                  <Section title="3a. Firemní údaje">
                    <Grid>
                      <Field label="Název firmy *" name="company_name" value={form.company_name} onChange={handleChange} placeholder="Firma s.r.o." required testid="order-company" />
                      <Field label="IČO" name="ico" value={form.ico} onChange={handleChange} placeholder="12345678" testid="order-ico" />
                      <Field label="Kontaktní osoba" name="contact_person" value={form.contact_person} onChange={handleChange} placeholder="Jan Novák" testid="order-contact-person" />
                      <Field label="Počet vozidel" name="vehicle_count" value={form.vehicle_count} onChange={handleChange} placeholder="např. 3" type="number" testid="order-vehicle-count" />
                    </Grid>

                    <div className="mt-5">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 mb-2">
                        Typ vozidel
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {VEHICLE_TYPE_OPTIONS.map((vt) => (
                          <button
                            key={vt.id}
                            type="button"
                            onClick={() => toggleArrayItem("vehicle_types", vt.id)}
                            data-testid={`order-vtype-${vt.id}`}
                            className={[
                              "rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors",
                              form.vehicle_types.includes(vt.id)
                                ? "border-amber-300/60 bg-amber-300/15 text-amber-100"
                                : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-amber-300/30",
                            ].join(" ")}
                          >
                            {vt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-5">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 mb-2">
                        Potřebuji
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {NEEDS_OPTIONS.map((n) => {
                          const active = form.biz_needs.includes(n.id);
                          return (
                            <button
                              key={n.id}
                              type="button"
                              onClick={() => toggleArrayItem("biz_needs", n.id)}
                              data-testid={`order-need-${n.id}`}
                              className={[
                                "rounded-xl border px-3.5 py-2.5 text-left text-xs font-medium transition-colors flex items-center gap-2",
                                active
                                  ? "border-amber-300/60 bg-amber-300/10 text-amber-100"
                                  : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-amber-300/30",
                              ].join(" ")}
                            >
                              <span className={[
                                "h-4 w-4 rounded-sm border flex items-center justify-center shrink-0",
                                active ? "border-amber-300 bg-amber-300/30" : "border-white/20",
                              ].join(" ")}>
                                {active && <CheckCircle2 className="h-3.5 w-3.5 text-amber-300" />}
                              </span>
                              {n.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Adresa vyzvednutí" name="pickup_address" value={form.pickup_address} onChange={handleChange} placeholder="Sídlo firmy, Znojmo" testid="order-pickup-address" />
                      <Field label="Adresa vrácení" name="return_address" value={form.return_address} onChange={handleChange} placeholder="Sídlo firmy, Znojmo" testid="order-return-address" />
                    </div>

                    <p className="mt-5 text-xs text-slate-500 leading-relaxed">
                      Odesláním formuláře nám předáváte poptávku. Termín,
                      dostupnost vyzvednutí, rozsah služby a případnou
                      servisní přípravu vám potvrdíme zpětně.
                    </p>
                  </Section>
                )}

                <Section title="4. Údaje o vozidle">
                  <Grid>
                    <Field label="Vozidlo (značka, model)" name="vehicle" value={form.vehicle} onChange={handleChange} placeholder="Škoda Octavia" testid="order-vehicle" />
                    <Field label="SPZ nebo VIN" name="spz_vin" value={form.spz_vin} onChange={handleChange} placeholder="1AB 2345" testid="order-spzvin" />
                    <Field label="Lokalita vozidla" name="location" value={form.location} onChange={handleChange} placeholder="Znojmo" testid="order-location" />
                    <Field label="Preferovaný termín" name="preferred_term" value={form.preferred_term} onChange={handleChange} placeholder="např. tento týden, pondělí dopoledne" testid="order-term" />
                  </Grid>
                </Section>

                <Section title="5. Částka k úhradě">
                  <div className="rounded-2xl border border-amber-300/15 bg-white/[0.03] p-4">
                    {requiresCustomAmount ? (
                      <>
                        <p className="text-sm text-slate-300">
                          Tato služba má individuální kalkulaci. Zadejte prosím částku, kterou jsme si s vámi domluvili po konzultaci s Lenkou.
                        </p>
                        <div className="mt-3 flex items-center gap-3">
                          <input
                            type="number" min="1" step="1"
                            name="custom_amount" value={form.custom_amount} onChange={handleChange}
                            placeholder="0"
                            data-testid="order-custom-amount"
                            className="w-40 rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none"
                          />
                          <span className="text-sm text-slate-400">Kč</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-xs text-slate-400 uppercase tracking-[0.18em]">Orientační částka</div>
                        <div className="font-heading text-3xl font-extrabold text-amber-200 mt-1">
                          {selected ? selected.price : "—"}
                        </div>
                        {selected?.parsed.from && (
                          <p className="mt-2 text-xs text-slate-400">
                            Můžete upravit částku, pokud máte domluvenou konkrétní cenu s Lenkou:
                          </p>
                        )}
                        <div className="mt-3 flex items-center gap-3">
                          <input
                            type="number" min="1" step="1"
                            name="custom_amount" value={form.custom_amount} onChange={handleChange}
                            placeholder={selected?.parsed.amount?.toString() || "0"}
                            data-testid="order-custom-amount"
                            className="w-40 rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none"
                          />
                          <span className="text-sm text-slate-400">Kč (nepovinné)</span>
                        </div>
                      </>
                    )}
                    {effectiveAmount > 0 && (
                      <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-300/10 border border-amber-300/30 px-3 py-2 text-sm">
                        K úhradě po vytvoření objednávky: <strong className="text-amber-200">{formatCzk(effectiveAmount)}</strong>
                      </div>
                    )}
                  </div>
                </Section>

                <Section title={
                  <button type="button" onClick={() => setShowBilling((v) => !v)} className="text-left w-full flex items-center justify-between" data-testid="order-toggle-billing">
                    <span>6. Fakturační údaje <span className="text-slate-500 text-xs font-normal">({isCompany ? "doplnit DIČ a adresu" : "volitelné"})</span></span>
                    <span className="text-xs text-amber-300/80">{showBilling ? "skrýt" : "zobrazit"}</span>
                  </button>
                }>
                  {showBilling && (
                    <Grid>
                      {!isCompany && (
                        <Field label="Název firmy" name="company_name" value={form.company_name} onChange={handleChange} placeholder="Firma s.r.o." className="sm:col-span-2" testid="order-company" />
                      )}
                      {!isCompany && (
                        <Field label="IČO" name="ico" value={form.ico} onChange={handleChange} placeholder="12345678" testid="order-ico" />
                      )}
                      <Field label="DIČ" name="dic" value={form.dic} onChange={handleChange} placeholder="CZ12345678" testid="order-dic" />
                      <Field label="Fakturační adresa" name="billing_address" value={form.billing_address} onChange={handleChange} placeholder="Ulice 1, 670 01 Znojmo" className="sm:col-span-2" testid="order-billing-address" />
                    </Grid>
                  )}
                </Section>

                <Section title="7. Poznámka">
                  <textarea
                    name="note" value={form.note} onChange={handleChange}
                    placeholder="Cokoli, co byste rádi sdělili Lence — preference, dotazy, urgence..."
                    rows={4} data-testid="order-note"
                    className="w-full rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none resize-y"
                  />
                </Section>

                <div className="rounded-2xl border border-amber-300/15 bg-white/[0.03] p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" name="gdpr_consent" checked={form.gdpr_consent} onChange={handleChange} data-testid="order-gdpr" className="mt-0.5 h-4 w-4 shrink-0 accent-amber-400" />
                    <span className="text-xs text-slate-300 leading-relaxed">
                      Souhlasím se zpracováním osobních údajů za účelem vyřízení této objednávky podle{" "}
                      <Link to="/ochrana-osobnich-udaju" target="_blank" rel="noopener noreferrer" className="text-amber-300 underline underline-offset-2">Zásad ochrany osobních údajů</Link>{" "}
                      a se zněním <Link to="/obchodni-podminky" target="_blank" rel="noopener noreferrer" className="text-amber-300 underline underline-offset-2">Obchodních podmínek</Link>.
                    </span>
                  </label>
                </div>

                {status === "error" && errorMsg && (
                  <div data-testid="order-error" className="flex items-start gap-3 rounded-xl border border-rose-400/30 bg-rose-400/10 p-4 text-rose-200 text-sm">
                    <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" /> <span>{errorMsg}</span>
                  </div>
                )}

                <button
                  type="submit" disabled={status === "loading" || !selected}
                  data-testid="order-submit"
                  className="w-full inline-flex items-center justify-center gap-3 rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed px-7 py-4 font-bold text-slate-900 transition-all"
                >
                  {status === "loading" ? (<><Loader2 className="h-4 w-4 animate-spin" /> Vytvářím objednávku…</>) : (<>Vytvořit objednávku a získat QR platbu <ArrowRight className="h-4 w-4" /></>)}
                </button>
              </form>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function PaymentSuccess({ result, onNew }) {
  const [copied, setCopied] = useState("");
  const copy = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1800);
  };

  return (
    <div data-testid="order-success">
      <div className="mt-8 flex items-center gap-3 text-emerald-300">
        <CheckCircle2 className="h-6 w-6" />
        <span className="font-heading text-lg font-bold">Objednávka přijata</span>
      </div>
      <h1 className="font-heading mt-3 text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
        Číslo objednávky: <span className="bg-gradient-to-b from-amber-200 to-amber-500 bg-clip-text text-transparent">{result.order_number}</span>
      </h1>
      <p className="mt-3 text-slate-400 max-w-2xl">
        Naskenujte QR kód v bankovní aplikaci, případně zaplaťte převodem podle údajů níže. Náš tým se vám ozve do hodiny.
      </p>

      <div className="mt-10 grid lg:grid-cols-12 gap-8">
        {/* QR */}
        <div className="lg:col-span-5">
          <div className="rounded-3xl border border-amber-300/30 bg-white p-6 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.35)]">
            <div className="text-center text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">QR Platba</div>
            <div className="mt-4 flex justify-center">
              <QRCodeSVG value={result.spayd} size={240} level="M" includeMargin data-testid="order-qr" />
            </div>
            <div className="mt-4 text-center">
              <div className="font-heading text-3xl font-extrabold text-slate-900">{formatCzk(result.amount)}</div>
              <div className="text-xs text-slate-500 mt-1">{result.currency}</div>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500 text-center">
            QR kód je ve standardu Czech QR Payment (SPAYD). Skenovatelný českými i slovenskými bankovními aplikacemi.
          </p>
        </div>

        {/* Payment details */}
        <div className="lg:col-span-7 space-y-3">
          <div className="rounded-2xl border border-amber-300/15 bg-white/[0.03] p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-300/80 font-semibold flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5" /> Příjemce platby
            </div>
            <div className="mt-2 text-base font-semibold text-white">{result.recipient_name}</div>
            <CopyRow label="Číslo účtu" value={result.account_number} k="acc" copied={copied} onCopy={copy} />
            <CopyRow label="IBAN" value={result.iban_pretty} k="iban" copied={copied} onCopy={copy} />
            <CopyRow label="BIC / SWIFT" value={result.bic} k="bic" copied={copied} onCopy={copy} />
          </div>

          <div className="rounded-2xl border border-amber-300/30 bg-amber-300/5 p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-300/80 font-semibold flex items-center gap-2">
              <Receipt className="h-3.5 w-3.5" /> Platební údaje
            </div>
            <CopyRow label="Variabilní symbol" value={result.variable_symbol} k="vs" copied={copied} onCopy={copy} highlight />
            <CopyRow label="Částka" value={`${result.amount.toFixed(2)} ${result.currency}`} k="am" copied={copied} onCopy={copy} highlight />
            <CopyRow label="Zpráva pro příjemce" value={result.payment_message} k="msg" copied={copied} onCopy={copy} />
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Shrnutí objednávky</div>
            <dl className="mt-3 space-y-1.5 text-sm">
              <Row k="Objednávka č." v={result.order_number} />
              <Row k="Služba" v={result.service_name} />
              <Row k="Kategorie" v={result.service_category} />
              <Row k="Zákazník" v={result.name} />
              <Row k="Telefon" v={result.phone} />
              {result.email && <Row k="E-mail" v={result.email} />}
              {result.vehicle && <Row k="Vozidlo" v={result.vehicle} />}
              {result.spz_vin && <Row k="SPZ / VIN" v={result.spz_vin} />}
              {result.preferred_term && <Row k="Preferovaný termín" v={result.preferred_term} />}
            </dl>
            <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-amber-300/30 bg-amber-300/10 px-3 py-1.5 text-xs text-amber-200">
              <ShieldCheck className="h-3.5 w-3.5" /> Stav: čeká na úhradu
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <a href={`tel:${PHONE.replace(/\s+/g, "")}`} className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 px-5 py-3 font-bold text-slate-900">
              <Phone className="h-4 w-4" /> Zavolat Lence
            </a>
            <Link to="/klient" className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-300/30 bg-amber-300/10 hover:bg-amber-300/20 px-5 py-3 font-semibold text-amber-200" data-testid="order-to-client-zone">
              Sledovat objednávku v Klientské zóně
            </Link>
            <button type="button" onClick={onNew} className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] px-5 py-3 font-semibold text-white" data-testid="order-new-btn">
              Nová objednávka
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CopyRow({ label, value, k, copied, onCopy, highlight }) {
  return (
    <div className="mt-2 flex items-center justify-between gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <div className={["mt-0.5 font-mono text-sm", highlight ? "text-amber-200 font-bold" : "text-white"].join(" ")}>{value}</div>
      </div>
      <button type="button" onClick={() => onCopy(value, k)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-300 hover:text-amber-300 hover:border-amber-300/40" data-testid={`order-copy-${k}`} aria-label={`Kopírovat ${label}`} title="Kopírovat">
        {copied === k ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-slate-400">{k}</dt>
      <dd className="text-white text-right">{v}</dd>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-5">
      <div className="text-xs uppercase tracking-[0.18em] text-amber-300/90 font-semibold">{title}</div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, name, value, onChange, placeholder, type = "text", required, className, testid }) {
  return (
    <div className={className || ""}>
      <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 mb-1.5">{label}</label>
      <input
        type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} required={required}
        data-testid={testid}
        className="w-full rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none transition"
      />
    </div>
  );
}
